import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Heart, Shield, TrendingUp, AlertTriangle, Clock,
  RefreshCw, Play, Pause, RotateCcw, ChevronRight, Zap,
  CheckCircle, XCircle, Timer, Thermometer
} from 'lucide-react';

const API = '/api';

const HEALTH_COLORS = {
  excellent: 'emerald',
  good: 'green',
  warning: 'yellow',
  critical: 'orange',
  danger: 'red',
  new: 'blue'
};

function HealthBadge({ health }) {
  const colorMap = {
    emerald: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    green: 'bg-green-500/10 text-green-500 border-green-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    red: 'bg-red-500/10 text-red-500 border-red-500/20',
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colorMap[health.color]}`}>
      {health.score}% - {health.status}
    </span>
  );
}

function WarmupProgress({ warmup }) {
  if (!warmup.enabled) return null;
  
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-zinc-500 flex items-center gap-1">
          <Thermometer className="w-3 h-3" />
          Warmup Día {warmup.day}/{warmup.total_days}
        </span>
        <span className="text-zinc-400">{warmup.current_daily_limit} msgs/día</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 ${warmup.complete ? 'bg-emerald-500' : 'bg-blue-500'}`}
          style={{ width: `${warmup.progress_percent}%` }}
        />
      </div>
      {warmup.complete && (
        <p className="text-[10px] text-emerald-500 mt-1 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Warmup completado
        </p>
      )}
    </div>
  );
}

function ProfileHealthCard({ profile, onRefresh }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profiles/${profile.id}/health`);
      setDetails(res.data);
    } catch (e) {
      toast.error('Error al cargar detalles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && !details) {
      loadDetails();
    }
  }, [expanded]);

  const handleWarmupToggle = async () => {
    try {
      await axios.post(`${API}/profiles/${profile.id}/warmup/toggle`, null, {
        params: { enabled: !details?.warmup?.enabled }
      });
      toast.success(details?.warmup?.enabled ? 'Warmup desactivado' : 'Warmup activado');
      loadDetails();
      onRefresh();
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleWarmupReset = async () => {
    try {
      await axios.post(`${API}/profiles/${profile.id}/warmup/reset`);
      toast.success('Warmup reiniciado');
      loadDetails();
      onRefresh();
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleClearCooldown = async () => {
    try {
      await axios.post(`${API}/profiles/${profile.id}/cooldown/clear`);
      toast.success('Cooldown limpiado');
      loadDetails();
      onRefresh();
    } catch (e) {
      toast.error('Error');
    }
  };

  const statusColor = profile.status === 'connected' ? 'bg-emerald-500' : 'bg-zinc-600';

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${statusColor}`} />
            <div>
              <h3 className="font-medium text-white">{profile.name}</h3>
              <p className="text-xs text-zinc-500">
                {profile.messages_today}/{profile.current_limit} msgs hoy
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HealthBadge health={profile.health} />
            {profile.in_cooldown && (
              <button
                onClick={(e) => { e.stopPropagation(); handleClearCooldown(); }}
                data-testid="clear-cooldown-quick-btn"
                className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-full hover:bg-red-500/30 flex items-center gap-1 font-medium border border-red-500/30 animate-pulse"
              >
                <Pause className="w-3 h-3" /> Cooldown ({Math.ceil(profile.cooldown_remaining / 60)}m) - LIMPIAR
              </button>
            )}
            {profile.warmup_day && !profile.warmup_complete && (
              <span className="px-2 py-1 bg-blue-500/10 text-blue-500 text-xs rounded-full">
                Día {profile.warmup_day}
              </span>
            )}
            <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950/50">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin w-6 h-6 border-2 border-zinc-700 border-t-blue-500 rounded-full" />
            </div>
          ) : details ? (
            <div className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-zinc-900 rounded-lg p-3">
                  <p className="text-xl font-bold text-white">{details.stats.messages_sent_24h}</p>
                  <p className="text-[10px] text-zinc-500">Enviados 24h</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-3">
                  <p className="text-xl font-bold text-red-500">{details.stats.messages_failed_24h}</p>
                  <p className="text-[10px] text-zinc-500">Fallidos 24h</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-3">
                  <p className="text-xl font-bold text-zinc-400">{details.stats.total_messages_sent}</p>
                  <p className="text-[10px] text-zinc-500">Total enviados</p>
                </div>
                <div className="bg-zinc-900 rounded-lg p-3">
                  <p className="text-xl font-bold text-orange-500">{details.rate_limit.consecutive_errors}</p>
                  <p className="text-[10px] text-zinc-500">Errores seguidos</p>
                </div>
              </div>

              {/* Warmup Section */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-blue-400" />
                    Warmup de Cuenta
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleWarmupToggle}
                      data-testid="warmup-toggle-btn"
                      className={`px-2 py-1 rounded text-xs ${
                        details.warmup.enabled 
                          ? 'bg-emerald-500/10 text-emerald-500' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {details.warmup.enabled ? 'Activo' : 'Inactivo'}
                    </button>
                    {details.warmup.enabled && (
                      <button
                        onClick={handleWarmupReset}
                        data-testid="warmup-reset-btn"
                        className="p-1 rounded bg-zinc-800 text-zinc-400 hover:text-white"
                        title="Reiniciar warmup"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                {details.warmup.enabled ? (
                  <>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-zinc-500">
                        Día {details.warmup.day} de {details.warmup.total_days}
                      </span>
                      <span className="text-zinc-400">
                        Límite actual: <span className="text-white font-medium">{details.warmup.current_daily_limit}</span> → {details.warmup.max_daily_limit}
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${details.warmup.complete ? 'bg-emerald-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}
                        style={{ width: `${details.warmup.progress_percent}%` }}
                      />
                    </div>
                    {details.warmup.complete && (
                      <p className="text-xs text-emerald-500 mt-2 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Warmup completado - Cuenta lista para envío máximo
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-zinc-500">
                    Warmup desactivado. Se usará el límite global de {details.warmup.current_daily_limit} msgs/día.
                  </p>
                )}
              </div>

              {/* Rate Limiting Section */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    Rate Limiting
                  </h4>
                  {details.rate_limit.in_cooldown && (
                    <button
                      onClick={handleClearCooldown}
                      data-testid="clear-cooldown-btn"
                      className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-xs hover:bg-red-500/20"
                    >
                      Limpiar Cooldown
                    </button>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Errores consecutivos</span>
                    <span className={`font-medium ${
                      details.rate_limit.consecutive_errors >= details.rate_limit.max_errors_before_pause 
                        ? 'text-red-500' 
                        : 'text-zinc-400'
                    }`}>
                      {details.rate_limit.consecutive_errors} / {details.rate_limit.max_errors_before_pause}
                    </span>
                  </div>
                  
                  {details.rate_limit.in_cooldown ? (
                    <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <Timer className="w-4 h-4 text-red-500" />
                      <div>
                        <p className="text-xs text-red-500 font-medium">En Cooldown</p>
                        <p className="text-[10px] text-red-400">
                          {Math.ceil(details.rate_limit.cooldown_remaining_seconds / 60)} minutos restantes
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <p className="text-xs text-emerald-500">Listo para enviar</p>
                    </div>
                  )}
                  
                  {details.stats.last_error && (
                    <div className="mt-2 p-2 bg-zinc-800 rounded text-xs">
                      <p className="text-zinc-500">Último error:</p>
                      <p className="text-zinc-400 truncate">{details.stats.last_error}</p>
                      <p className="text-zinc-600 text-[10px]">{new Date(details.stats.last_error_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function HealthMonitor() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profiles/health/all`);
      setProfiles(res.data);
    } catch (e) {
      toast.error('Error al cargar perfiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadProfiles, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate summary stats
  const summary = {
    total: profiles.length,
    healthy: profiles.filter(p => ['excellent', 'good'].includes(p.health.status)).length,
    warning: profiles.filter(p => p.health.status === 'warning').length,
    critical: profiles.filter(p => ['critical', 'danger'].includes(p.health.status)).length,
    inCooldown: profiles.filter(p => p.in_cooldown).length,
    inWarmup: profiles.filter(p => p.warmup_day && !p.warmup_complete).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Activity className="w-7 h-7 text-emerald-500" />
            Monitor de Salud
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Warmup, rate limiting y estado de tus perfiles
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/profiles/cooldown/clear-all`);
                toast.success('Todos los cooldowns y errores limpiados');
                loadProfiles();
              } catch (e) {
                toast.error('Error al limpiar cooldowns');
              }
            }}
            data-testid="clear-all-cooldowns-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg transition-colors text-sm"
          >
            <Pause className="w-4 h-4" />
            Limpiar Cooldowns
          </button>
          <button
            onClick={loadProfiles}
            disabled={loading}
            data-testid="health-refresh-btn"
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary.inCooldown > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between" data-testid="cooldown-alert-banner">
          <div className="flex items-center gap-3">
            <Pause className="w-6 h-6 text-red-400 animate-pulse" />
            <div>
              <p className="text-sm font-medium text-red-400">{summary.inCooldown} perfil(es) en Cooldown</p>
              <p className="text-xs text-red-400/70">Los envios estan pausados por errores previos</p>
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/profiles/cooldown/clear-all`);
                toast.success('Todos los cooldowns limpiados');
                loadProfiles();
              } catch (e) {
                toast.error('Error al limpiar cooldowns');
              }
            }}
            data-testid="clear-all-cooldowns-btn"
            className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm rounded-lg transition-colors font-medium"
          >
            Limpiar Todos los Cooldowns
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="health-total-profiles">
          <p className="text-2xl font-bold text-white">{summary.total}</p>
          <p className="text-xs text-zinc-500">Total Perfiles</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="health-healthy-count">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-500">{summary.healthy}</p>
          </div>
          <p className="text-xs text-zinc-500">Saludables</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <p className="text-2xl font-bold text-yellow-500">{summary.warning}</p>
          </div>
          <p className="text-xs text-zinc-500">Advertencia</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <p className="text-2xl font-bold text-red-500">{summary.critical}</p>
          </div>
          <p className="text-xs text-zinc-500">Críticos</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="health-cooldown-count">
          <div className="flex items-center gap-2">
            <Pause className="w-5 h-5 text-orange-500" />
            <p className="text-2xl font-bold text-orange-500">{summary.inCooldown}</p>
          </div>
          <p className="text-xs text-zinc-500">En Cooldown</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="health-warmup-count">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <p className="text-2xl font-bold text-blue-500">{summary.inWarmup}</p>
          </div>
          <p className="text-xs text-zinc-500">En Warmup</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-400">Sistema Anti-Ban Activo</p>
            <p className="text-xs text-blue-400/70 mt-1">
              <strong>Warmup:</strong> Los nuevos perfiles comienzan con 5 msgs/día y aumentan gradualmente hasta 200/día en 14 días.
              <br />
              <strong>Rate Limiting:</strong> Después de 3 errores consecutivos, el perfil entra en cooldown automático (5-120 min).
            </p>
          </div>
        </div>
      </div>

      {/* Profiles List */}
      {loading && profiles.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin w-8 h-8 border-2 border-zinc-700 border-t-blue-500 rounded-full mb-4" />
            <p className="text-zinc-500">Cargando perfiles...</p>
          </div>
        </div>
      ) : profiles.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <Activity className="w-12 h-12 text-zinc-700 mb-4" />
            <p className="text-zinc-500">No hay perfiles</p>
            <p className="text-xs text-zinc-600 mt-1">Crea un perfil para comenzar</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map(profile => (
            <ProfileHealthCard 
              key={profile.id} 
              profile={profile} 
              onRefresh={loadProfiles}
            />
          ))}
        </div>
      )}
    </div>
  );
}
